import {
  getMetricColorForMetric,
  resolveThreshold,
} from "@/metrics/metric-display"
import type { MetricRecord } from "@/metrics/runtime"

import {
  MAX_SECTION_ITEMS,
  MIN_HEADLINE_METRIC_COVERAGE_SHARE,
  MIN_HEADLINE_METRIC_MEASUREMENTS,
  MIN_SIGNIFICANT_METRIC_ABSOLUTE_DELTA,
  MIN_SIGNIFICANT_METRIC_RELATIVE_DELTA,
} from "./runsSummaryConstants"
import { buildCoverageComparison } from "./runsSummaryCoverage"
import {
  getRowsWithMetricValue,
  metricMatchesFocus,
} from "./runsSummaryDetailData"
import {
  formatCount,
  formatMetricNumber,
  formatPercent,
  pluralize,
} from "./runsSummaryFormat"
import type {
  CoverageComparison,
  MetricDirection,
  MetricWindowStats,
  RunsPageSummaryRow,
  RunsSummaryFocus,
} from "./runsSummaryTypes"

export function getMetricDirection(
  label: string,
  metricRecord?: MetricRecord,
  isBinary = false,
): MetricDirection {
  if (metricRecord && resolveThreshold(metricRecord)) {
    try {
      const colorRank = { danger: 0, warning: 1, default: 2, success: 3 }
      const lowRank =
        colorRank[getMetricColorForMetric(0, metricRecord, isBinary)]
      const highRank =
        colorRank[getMetricColorForMetric(1, metricRecord, isBinary)]
      if (highRank !== lowRank) {
        return highRank > lowRank ? "positive" : "negative"
      }
    } catch {
      // Fall through to the label heuristic for incomplete metric records.
    }
  }

  if (
    /success|integrity|adherence|accuracy|quality|score|passed|pass/i.test(
      label,
    )
  ) {
    return "positive"
  }

  if (
    /latency|ttfb|anomal|error|fail|refusal|repeat|reprompt|dropout|clipping|artifact/i.test(
      label,
    )
  ) {
    return "negative"
  }

  return "neutral"
}

function getMetricAssessment(
  metric: MetricWindowStats,
  delta: number,
  coverageComparison: CoverageComparison,
): "better" | "worse" | null {
  if (!coverageComparison.isLikeForLike) {
    return null
  }

  const direction = getMetricDirection(
    metric.label,
    metric.metricRecord,
    metric.isBinaryLike,
  )
  if (direction === "negative") {
    return delta > 0 ? "worse" : "better"
  }
  if (direction === "positive") {
    return delta > 0 ? "better" : "worse"
  }
  return null
}

function formatCoverageMixPhrase(changedDimensions: string[]): string {
  if (
    changedDimensions.length === 0 ||
    changedDimensions.includes("coverage")
  ) {
    return "coverage mix changed"
  }
  return `${changedDimensions.join("/")} mix changed`
}

export function isPriorityMetric(label: string): boolean {
  return /latency|duration|turn|pause|syllable|success|integrity|tool|anomal|error|fail|refusal|repeat|reprompt|transcription/i.test(
    label,
  )
}

export function getMetricDeltaMagnitude(metric: MetricWindowStats): number {
  if (metric.currentAverage === null || metric.previousAverage === null) {
    return 0
  }
  const absoluteDelta = Math.abs(metric.currentAverage - metric.previousAverage)
  const relativeDelta =
    Math.abs(metric.previousAverage) > 0
      ? absoluteDelta / Math.abs(metric.previousAverage)
      : absoluteDelta
  return Math.max(absoluteDelta, relativeDelta)
}

export function isSignificantMetricMovement(
  metric: MetricWindowStats,
): boolean {
  if (
    metric.currentAverage === null ||
    metric.previousAverage === null ||
    metric.currentCount === 0 ||
    metric.previousCount === 0
  ) {
    return false
  }
  const absoluteDelta = Math.abs(metric.currentAverage - metric.previousAverage)
  const relativeDelta =
    Math.abs(metric.previousAverage) > 0
      ? absoluteDelta / Math.abs(metric.previousAverage)
      : absoluteDelta

  return (
    absoluteDelta >= MIN_SIGNIFICANT_METRIC_ABSOLUTE_DELTA &&
    relativeDelta >= MIN_SIGNIFICANT_METRIC_RELATIVE_DELTA
  )
}

export function getMinimumHeadlineMetricCount(
  scorableSimulations: number,
): number {
  return Math.min(
    scorableSimulations,
    Math.max(
      MIN_HEADLINE_METRIC_MEASUREMENTS,
      Math.ceil(scorableSimulations * MIN_HEADLINE_METRIC_COVERAGE_SHARE),
    ),
  )
}

function getMetricConcernShare(metric: MetricWindowStats): number {
  if (
    !metric.isBinaryLike ||
    metric.currentAverage === null ||
    getMetricDirection(
      metric.label,
      metric.metricRecord,
      metric.isBinaryLike,
    ) === "neutral"
  ) {
    return 0
  }

  return getMetricDirection(
    metric.label,
    metric.metricRecord,
    metric.isBinaryLike,
  ) === "positive"
    ? 1 - metric.currentAverage
    : metric.currentAverage
}

export function compareExactHeadlineMetrics(
  a: MetricWindowStats,
  b: MetricWindowStats,
  focus: RunsSummaryFocus,
): number {
  const aAssessment =
    a.currentAverage !== null && a.previousAverage !== null
      ? getMetricAssessment(a, a.currentAverage - a.previousAverage, {
          isLikeForLike: true,
          changedDimensions: [],
        })
      : null
  const bAssessment =
    b.currentAverage !== null && b.previousAverage !== null
      ? getMetricAssessment(b, b.currentAverage - b.previousAverage, {
          isLikeForLike: true,
          changedDimensions: [],
        })
      : null
  const worseMovementDelta =
    Number(bAssessment === "worse" && isSignificantMetricMovement(b)) -
    Number(aAssessment === "worse" && isSignificantMetricMovement(a))
  const concernDelta = getMetricConcernShare(b) - getMetricConcernShare(a)
  const focusDelta =
    Number(metricMatchesFocus(b.id, b.label, focus)) -
    Number(metricMatchesFocus(a.id, a.label, focus))

  return (
    worseMovementDelta ||
    concernDelta ||
    focusDelta ||
    b.currentCount - a.currentCount
  )
}

function formatMetricAverage(metric: MetricWindowStats, value: number): string {
  if (metric.isBinaryLike) {
    return formatPercent(value)
  }
  return formatMetricNumber(value)
}

export function buildMetricMovementItem(
  metric: MetricWindowStats,
  coverageComparison: CoverageComparison,
  includeCoverageCaveat = true,
  formatAverage = formatMetricAverage,
): string | null {
  if (metric.currentAverage === null || metric.previousAverage === null) {
    return null
  }
  const delta = metric.currentAverage - metric.previousAverage
  const direction = delta > 0 ? "up" : "down"
  const assessment = getMetricAssessment(metric, delta, coverageComparison)
  const metricDirection = getMetricDirection(
    metric.label,
    metric.metricRecord,
    metric.isBinaryLike,
  )
  const caveat =
    assessment === null &&
    !coverageComparison.isLikeForLike &&
    includeCoverageCaveat
      ? `; ${formatCoverageMixPhrase(
          coverageComparison.changedDimensions,
        )}, so compare like-for-like before interpreting`
      : assessment === null &&
          coverageComparison.isLikeForLike &&
          metricDirection === "neutral"
        ? "; this is not inherently good or bad"
        : ""

  return `${metric.label} averaged ${formatAverage(
    metric,
    metric.currentAverage,
  )}, ${direction} from ${formatAverage(
    metric,
    metric.previousAverage,
  )}${assessment ? ` (${assessment})` : ""}${caveat}.`
}

export function buildMetricConcernItem(
  metric: MetricWindowStats,
  rowNoun: string,
): string | null {
  if (metric.currentCount === 0 || metric.currentAverage === null) {
    return null
  }

  if (metric.currentDangerCount > 0) {
    return `${metric.label} has ${formatCount(
      metric.currentDangerCount,
    )} concerning ${pluralize(
      metric.currentDangerCount,
      "output",
    )} across ${formatCount(metric.currentCount)} measured ${pluralize(
      metric.currentCount,
      rowNoun,
    )} (${formatPercent(metric.currentDangerCount / metric.currentCount)}).`
  }

  if (
    metric.isBinaryLike &&
    getMetricDirection(
      metric.label,
      metric.metricRecord,
      metric.isBinaryLike,
    ) === "positive" &&
    metric.currentAverage < 0.95
  ) {
    return `${metric.label} is at ${formatPercent(
      metric.currentAverage,
    )} across ${formatCount(metric.currentCount)} measured ${pluralize(
      metric.currentCount,
      rowNoun,
    )}.`
  }

  if (
    metric.isBinaryLike &&
    getMetricDirection(
      metric.label,
      metric.metricRecord,
      metric.isBinaryLike,
    ) === "negative" &&
    metric.currentAverage > 0
  ) {
    return `${metric.label} appeared in ${formatPercent(
      metric.currentAverage,
    )} of ${formatCount(metric.currentCount)} measured ${pluralize(
      metric.currentCount,
      rowNoun,
    )}.`
  }

  return null
}

export function buildFocusItems({
  currentRows,
  focus,
  metricStats,
  previousRows,
  rowNoun,
}: {
  currentRows: RunsPageSummaryRow[]
  focus: RunsSummaryFocus
  metricStats: MetricWindowStats[]
  previousRows: RunsPageSummaryRow[]
  rowNoun: string
}): string[] {
  if (!focus.hasFocus) {
    return []
  }

  const items: string[] = []
  const focusedMovement = metricStats
    .filter(isSignificantMetricMovement)
    .find((metric) => metricMatchesFocus(metric.id, metric.label, focus))
  const focusedConcern = metricStats
    .filter((metric) => metricMatchesFocus(metric.id, metric.label, focus))
    .map((metric) => buildMetricConcernItem(metric, rowNoun))
    .find((item): item is string => item !== null)

  if (focusedMovement) {
    const movementItem = buildMetricMovementItem(
      focusedMovement,
      buildCoverageComparison(
        getRowsWithMetricValue(currentRows, focusedMovement.id),
        getRowsWithMetricValue(previousRows, focusedMovement.id),
      ),
    )
    if (movementItem) {
      items.push(`Matched metric movement: ${movementItem}`)
    }
  }

  if (focusedConcern) {
    items.push(`Matched metric concern: ${focusedConcern}`)
  }

  return items.slice(0, MAX_SECTION_ITEMS)
}
